import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import finalizeSteps from "./prefilled-generation-finalize.json";
import finalizeStepsVibeCoding from "./prefilled-generation-finalize-vibe-coding.json";
import * as fromStore from "../../../../../store";
import { filter, first, switchMap, takeUntil } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { UserService } from "../../../../../user/user.service";
import { combineLatest, Subject } from "rxjs";
import { BuilderSectionsInteractionService } from "../../../../builder-sections-interaction.service";
import { DbModalService } from "../../../../../modules/modal/modal";
import { User } from "@sentry/browser";
import { GoogleAnalyticsService } from "../../../../../shared/services/google-analytics/google-analytics.service";
import { ActivatedRoute } from "@angular/router";
import { BuilderHistoryService } from "../../../../builder-history.service";
import {Config} from "../../../../../config/config";
import {CookieService} from "ngx-cookie";
import {CommonBuilderDataService} from "../../../../services/common-builder-data.service";
import {VibeCodingFlowService} from "../../../../../shared/services/vibe-coding-flow.service";
import {Website} from "../../../../../websites/website/website";

type createAIFinalizeStepStatus = "todo" | "process" | "passed";

interface ICreateAIFinalizeStep {
  label: string;
  status: createAIFinalizeStepStatus | string;
  description: string;
  isEcommerce?: boolean;
}

@Component({
  selector: "db-prefilled-generation-finalize",
  templateUrl: "./prefilled-generation-finalize.component.html",
  styleUrls: ["./prefilled-generation-finalize.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PrefilledGenerationFinalizeComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() user: User;
  @Input() uniqueId: string;
  @Input() website: Website;
  @Input() isEcommerce: boolean;

  steps: ICreateAIFinalizeStep[];
  userLocalStorage: any;
  updateLocalStorage: boolean = false;
  loading: boolean = false;
  isMobile: boolean = false;
  vibeCodingFlow: boolean = false;
  vibeCodingFlowMobile: boolean = false;
  progress = 0;
  private stepDurations: number[] = [];
  private stepPercentages: number[] = [];

  private stepTargets = [20, 30, 60, 90, 98];
  private counter: number = -1;
  private interval!: ReturnType<typeof setInterval>;
  private _progressInterval: any;
  private _destroyStream$ = new Subject<boolean>();
  private oldProgressValue = 0;

  constructor(
    private _userService: UserService,
    private _store: Store<fromStore.DbStoreState>,
    public builderInteractionsService: BuilderSectionsInteractionService,
    public _builderHistoryService: BuilderHistoryService,
    public _modalService: DbModalService,
    private _cdr: ChangeDetectorRef,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _vibeCodingFlowService: VibeCodingFlowService
  ) {
    this.uniqueId = this._builderHistoryService.getUniqueId();
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 991;
    this.vibeCodingFlow = this._vibeCodingFlowService.isVibeCodingFlow() || this.website?.isWVC;

    this.steps = this.isEcommerce
      ? finalizeSteps
      : (this.vibeCodingFlow ? finalizeStepsVibeCoding : finalizeSteps.filter(step => step.isEcommerce !== true));
    this._getCompanyInfo();
    this._initSteps();
    this._subscribeToLoading();

    // Generate random step durations (ensuring at least 50s total)
    this._generateRandomDurations();

    // Generate random progress percentages
    this._generateDistributedPercentages();

    // Start the first step
    this._toNextStep();
    this._getUserLocalStorage();
  }

  ngAfterViewInit() {
    this._cdr.detectChanges();
  }

  ngOnDestroy() {
    clearInterval(this.interval);
    clearInterval(this._progressInterval);
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _initSteps() {
    this.steps.forEach((step, i) => {
      step.status = i === 0 ? "process" : "todo";
    });

    // Optionally start from 1% (so user never sees 0%)
    this.progress = 1;
    this.oldProgressValue = 1;
    this._cdr.markForCheck();
  }

  private _generateRandomDurations() {
    const totalSteps = this.steps.length;
    const minTotalTime = 70000;
    let assignedDurations: number[] = [];

    const durationRatios = [0.25, 0.25, 0.2, 0.15, 0.1, 0.05]; // Must sum to 1
    const durationRatiosVibeCoding = [0.05, 0.05, 0.7, 0.2]; // Must sum to 1

    for (let i = 0; i < totalSteps; i++) {
      const stepTime = Math.floor(minTotalTime * ((this.vibeCodingFlow || this.vibeCodingFlowMobile) ? durationRatiosVibeCoding[i] : durationRatios[i]));
      assignedDurations.push(stepTime);
    }

    this.stepDurations = assignedDurations;
  }

  private _generateDistributedPercentages() {
    const totalSteps = this.steps.length;
    this.stepPercentages = [];

    const basePercentages = [20, 35, 50, 70, 90];

    for (let i = 0; i < totalSteps - 1; i++) {
      this.stepPercentages.push(basePercentages[i]);
    }

    // Last step goes to 98%
    this.stepPercentages.push(98);
  }

  private _subscribeToLoading(): void {
    this.builderInteractionsService.buildLoading
      .pipe(takeUntil(this._destroyStream$))
      .subscribe((isLoading) => {
        this.loading = isLoading;

        if (
          !isLoading &&
          this.steps[this.steps.length - 1]?.status === "process"
        ) {
          // Final step done → move from 98% → 100% smoothly
          this.steps[this.steps.length - 1].status = "passed";
          this._animateProgressOverDuration(98, 100, 500);
        }

        this._cdr.detectChanges();
      });
  }

  private _toNextStep() {
    const stepsLength = (this.vibeCodingFlow || this.vibeCodingFlowMobile) ? this.steps.length - 2 : this.steps.length - 1;

    const processStep = () => {
      this.counter++;

      if (this.counter > stepsLength) {
        clearInterval(this.interval);
        return;
      }

      // Update step statuses
      if (this.counter > 0) {
        this.steps[this.counter - 1].status = "passed";
      }
      this.steps[this.counter].status = "process";

      // Calculate start and target percentages for this step
      const startProgress =
        this.counter === 0 ? 1 : this.stepPercentages[this.counter - 1];
      const targetProgress = this.stepPercentages[this.counter];
      const stepDuration = this.stepDurations[this.counter];

      // Animate progress for this step
      this._animateProgressOverDuration(
        startProgress,
        targetProgress,
        stepDuration
      );

      this._cdr.markForCheck();

      // Schedule next step if not the last one
      if (this.counter < stepsLength) {
        setTimeout(processStep, stepDuration);
      }
    };

    processStep();
  }

  private _animateProgressOverDuration(
    startValue: number,
    endValue: number,
    duration: number
  ) {
    clearInterval(this._progressInterval);

    const totalSteps = endValue - startValue;
    if (totalSteps <= 0) return;

    const isLastStep = this.counter === this.steps.length - 1;

    // Calculate interval time to use full step duration
    const intervalTime = Math.max(duration / totalSteps, 50);

    let currentValue = startValue;

    this._progressInterval = setInterval(() => {
      if (currentValue >= endValue) {
        clearInterval(this._progressInterval);
        this.progress =
          endValue === 100 && !this.loading ? 100 : Math.min(endValue, 98);
        this._cdr.markForCheck();
        return;
      }

      currentValue++;
      this.progress = !this.loading ? Math.min(currentValue, 98) : currentValue;
      this._cdr.markForCheck();
    }, intervalTime);
  }

  private _getUserLocalStorage(): void {
    this._store
      .select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() =>
          combineLatest(
            this._store.select(fromStore.getUserLocalStorage),
            this._store.select(fromStore.getUserMeta())
          )
        ),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([userLocalStorage, meta]) => {
        this.userLocalStorage = userLocalStorage;
        !this.updateLocalStorage && this._updateUserLocalStorage();
      });
  }

  private _updateUserLocalStorage(): void {
    if (localStorage.getItem("dbOnboardingDomainName")) {
      this._checkSuggestedDomain(
        localStorage.getItem("dbOnboardingDomainName")
      );
      localStorage.removeItem("dbOnboardingDomainName");
      localStorage.removeItem("dbOnboardingDomainNameInitial");
      localStorage.removeItem("dbOnboardingDomainInfo");
    } else {
      this._updateLocalStorage();
    }
  }

  private _checkSuggestedDomain(domainName) {
    this.userLocalStorage["suggestedDomainNameInitial"] = localStorage.getItem(
      "dbOnboardingDomainNameInitial"
    );
    this.userLocalStorage["suggestedDomainName"] = domainName;
    this._updateLocalStorage();
  }

  private _updateLocalStorage(): void {
    this._userService
      .setToLocal(this.userLocalStorage)
      .pipe(first((response) => !!response, null))
      .subscribe((response) => {
        if (response.success) {
          this.updateLocalStorage = true;
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
          this._store.dispatch(new fromStore.LoadUser());
        }
      });
  }

  private _sendEvent(category: string, action: string, label: string): void {
    const analyticsData = {
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      uniqueId: this.uniqueId,
      isMobile: this.isMobile,
    };

    if (!this.user) {
      this._googleAnalyticsService.sendAnalyticsEventWithCoreWithoutUserData(
        analyticsData
      );
    } else {
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    }
  }

  private _getCompanyInfo(): void {
    this._store.select(fromStore.getCompanyInfoLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getCompanyInfo)),
        first()
      )
      .subscribe((companyInfo: any) => {
        const companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || '10Web';
        // Update the first step description with the company name for vibe coding flow
        if (this.steps && this.steps.length > 0 && this.vibeCodingFlow) {
          this.steps[0].description = `Setting up secure ${companyName} hosting for your site`;
        }
        this._cdr.markForCheck();
      });
  }
}
